/* ENSSA Reporting — expanded-row subtest profile (flat rows).
   Rendered inside ClassPage's expanded student row. The earlier
   nested-card grid read as boxes-in-boxes inside the parent class
   table; this flat list (Option A) drops the per-subtest card,
   shrinks the 36 px full-width pill to a compact inline chip, and
   lays out one row per subtest so the chip column reads as a
   straight vertical scan across the five subtests. */

import { TERM_DEFAULT, getBattery } from "@/data/tokens";
import { formatCompactTaskEvidence, taskBandLabel } from "@/lib/copy";
import { resolveStudentOutcomes } from "@/lib/task-family-bands";
import type { ClassRoom, Student, TaskFamilyBand, TermId } from "@/types";

interface StudentSubtestCardsProps {
  student: Student;
  klass: ClassRoom;
  term: TermId | string;
}

/* Classified bands share the global risk palette; the grey evidence
   states keep the subtest-only treatment. */
const BAND_PILL: Record<TaskFamilyBand, { bg: string; fg: string }> = {
  very_low: { bg: "var(--risk-concern-bg)", fg: "var(--risk-concern-fg)" },
  low: { bg: "var(--risk-emerging-bg)", fg: "var(--risk-emerging-fg)" },
  on_track: { bg: "var(--risk-on-track-bg)", fg: "var(--risk-on-track-fg)" },
  limited: { bg: "var(--task-limited-bg)", fg: "var(--task-limited-fg)" },
  not_attempted: { bg: "var(--task-limited-bg)", fg: "var(--task-limited-fg)" },
};

/* Colour dot inside the chip — mirrors TaskBandBadge so every band
   chip pairs colour + label the same way. Evidence states get the
   dashed-grey dot treatment. */
const BAND_DOT: Record<TaskFamilyBand, { background: string; border?: string }> = {
  very_low: { background: "var(--risk-concern)" },
  low: { background: "var(--risk-emerging)" },
  on_track: { background: "var(--risk-on-track)" },
  limited: {
    background: "var(--task-limited)",
    border: "1px dashed var(--task-limited-fg)",
  },
  not_attempted: {
    background: "var(--task-limited-bg)",
    border: "1px dashed var(--task-limited-fg)",
  },
};

/* Chip column is fixed-width so the widest label ("Too few responses")
   never wraps. Width chosen empirically to fit it with the colour dot
   at fs-12 + bold weight, plus chip padding, with a small buffer. */
const CHIP_COL_WIDTH = 168;

export function StudentSubtestCards({ student, klass, term }: StudentSubtestCardsProps) {
  const battery = getBattery(
    student.year,
    student.cohort || klass?.cohort || "A",
    term || TERM_DEFAULT,
  );
  const outcomes = resolveStudentOutcomes(student, battery, term);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {outcomes.map(({ task, outcome }, i) => {
        const pill = BAND_PILL[outcome.band];
        const dot = BAND_DOT[outcome.band];
        const evidence = formatCompactTaskEvidence(outcome).join(" · ");
        return (
          <div
            key={task.id}
            style={{
              display: "grid",
              gridTemplateColumns: `minmax(220px, 360px) ${CHIP_COL_WIDTH}px minmax(0, 1fr)`,
              alignItems: "center",
              columnGap: 16,
              padding: "9px 4px",
              borderTop: i === 0 ? 0 : "1px solid var(--border-subtle)",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: "var(--fs-14)",
                color: "var(--fg-1)",
                lineHeight: "var(--lh-snug)",
              }}
            >
              {task.label}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: "var(--r-pill)",
                  background: pill.bg,
                  color: pill.fg,
                  fontWeight: 700,
                  fontSize: "var(--fs-12)",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    flexShrink: 0,
                    ...dot,
                  }}
                />
                <span>{taskBandLabel(outcome.band)}</span>
              </span>
            </div>
            <div
              style={{
                fontSize: "var(--fs-12)",
                color: "var(--fg-3)",
                lineHeight: 1.5,
              }}
            >
              {evidence}
            </div>
          </div>
        );
      })}
    </div>
  );
}
